import React, { useState, useEffect, useMemo } from 'react';
import { Autocomplete, TextField, Typography, Button } from '@mui/material';
import { Warning } from '@mui/icons-material';

const labelStyle = {
  fontSize: '12px',
  fontWeight: 400,
  color: '#1E1E1E',
  textAlign: 'start'
};

const headerStyle = {
  color: "var(--hti-magenta)",
  fontSize: "13px",
  fontWeight: 500,
  marginBottom: "10px",
  textAlign: 'start'
}

const inputFieldStyle = {
  width: '100%',
  input: {
    fontSize: '12px',
    paddingLeft: '5px',
  },
};

const listProps = {
  sx: {
    fontSize: '12px',
  },
};

const getSubCategoryOptions = (data) => {
  let optionArray = Object.keys(data).map(m => {
    return {
      label: m,
      id: m,
      disable: !data[m]?.enabled
    }
  });
  let enabledOptions = optionArray.filter(m => !m.disable);
  let disabledOptions = optionArray.filter(m => m.disable);
  return [...enabledOptions, ...disabledOptions]
}

const DocumentSplitForm = ({ 
  splitDetails = {}, 
  splitData = [], 
  onChange, 
  addSplitData, 
  error, 
  isEdit = false,
  data={},
  classificationMappingData = {},
  splitProgressPercentage
}) => {

  const patientNameList = useMemo(() => {
    return (data.Entity_Details?.["additionalPatientName"] || []).map(m => ({ label: m.value, id: m.value }));
  }, [data?.Entity_Details?.["additionalPatientName"]]);

  const patientDOBList = useMemo(() => {
    return (data.Entity_Details?.["additionalDateOfBirth"] || []).map(m => ({ label: m.value, id: m.value }));
  }, [data?.Entity_Details?.["additionalDateOfBirth"]]);

  const subCategoryList = useMemo(() => {
    let optionArray = Object.keys(classificationMappingData).map(m => {
      return {
        ...classificationMappingData[m],
        label: m,
        id: m,
      }
    });
    let enabledOptions = optionArray.filter(m => m.enabled);
    let disabledOptions = optionArray.filter(m => !m.enabled);
    return [...enabledOptions, ...disabledOptions];
  }, [classificationMappingData]);

  const disableForm = useMemo(() => {
    if (isEdit == true) return false;
    else if (splitProgressPercentage == 100 || isNaN(splitProgressPercentage)) return true;
  }, [splitProgressPercentage, isEdit, splitData]);

  const formDetails = useMemo(() => {
    let obj = {};
    let nextPageNum = (splitData || []).reduce((acc, m) => {
      if (acc < m.end) acc = m.end;
      return acc;
    }, 0);
    if (isEdit) obj["start"] = splitDetails.start;
    else obj["start"] = splitDetails.start || splitDetails.start == '' ? splitDetails.start : (Number(nextPageNum) + 1);

    if (disableForm) return { start: "", end: "", patient_name: "", dob: "", sub_category: "" }
    return { ...splitDetails, ...obj }
  }, [splitDetails, splitData, disableForm]);

  const handleChange = (col, val) => {
    typeof onChange === 'function' && onChange(col, val);
  };

  const handleAddButton = () => {
    let tempData = JSON.parse(JSON.stringify(formDetails));
    let selectedSub = subCategoryList.find(m => m.id == formDetails.sub_category) || {};
    if(selectedSub.category) {
      tempData.category = selectedSub.category.display_name;
    } else {
      tempData.category = "";
    }
    typeof addSplitData === "function" && addSplitData(tempData, isEdit ? "update" : "add");
  }

  return (
    <div className="doc-split-form-container">
      <Typography sx={{ ...headerStyle }}>Document Registration</Typography>
      <div className="doc-split-form-items">
        <div className="doc-split-form-item">
          <Typography sx={labelStyle}>Patient Name</Typography>
          <Autocomplete
            options={patientNameList || []}
            name="patient_name"
            freeSolo={true}
            forcePopupIcon={true}
            value={formDetails?.patient_name || ""}
            onInputChange={(e, newValue, reason) => handleChange('patient_name', newValue)}
            getOptionDisabled={(option) => option?.disable}
            sx={inputFieldStyle}
            ListboxProps={listProps}
            disabled={disableForm}
            renderInput={(params) => (
              <TextField {...params} variant="standard" />
            )}
          />
        </div>
        <div className="doc-split-form-item">
          <Typography sx={labelStyle}>Date of Birth</Typography>
          <Autocomplete
            options={patientDOBList || []}
            name="dob"
            freeSolo={true}
            forcePopupIcon={true}
            value={formDetails?.dob || ""}
            onInputChange={(e, newValue, reason) => handleChange('dob', newValue)}
            getOptionDisabled={(option) => option?.disable}
            sx={inputFieldStyle}
            ListboxProps={listProps}
            disabled={disableForm}
            renderInput={(params) => (
              <TextField {...params} variant="standard" />
            )}
          />
        </div>
        <div className="doc-split-form-item">
          <Typography sx={labelStyle}>Sub Category</Typography>
          <Autocomplete
            options={subCategoryList || []}
            name="sub_category"
            freeSolo={false}
            forcePopupIcon={true}
            value={formDetails?.sub_category || ""}
            onInputChange={(e, newValue, reason) => (reason == "selectOption" || reason == "clear") && handleChange('sub_category', newValue)}
            getOptionDisabled={(option) => !option?.enabled}
            sx={inputFieldStyle}
            ListboxProps={listProps}
            disabled={disableForm}
            renderInput={(params) => (
              <TextField {...params} variant="standard" />
            )}
          />
        </div>
        <div className="doc-split-form-item">
          <Typography sx={labelStyle}>Range</Typography>
          <div className="range-field">
            <TextField
              id="outlined-number"
              variant="standard"
              sx={{ ...inputFieldStyle }}
              type="number"
              placeholder="Start"
              value={formDetails?.start <= data?.Page_Count && formDetails?.start > 0 ? formDetails?.start : ""}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{ min: 1, max: data?.Page_Count }}
              disabled={disableForm}
              onChange={(e) => handleChange('start', e.target.value)}
            />
            <TextField
              id="outlined-number"
              variant="standard"
              sx={{ ...inputFieldStyle }}
              type="number"
              placeholder="End"
              value={formDetails?.end <= data?.Page_Count && formDetails?.end > 0 ? formDetails?.end : ""}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{ min: 1, max: data?.Page_Count }}
              disabled={disableForm}
              onChange={(e) => handleChange('end', e.target.value)}
            />
          </div>
        </div>
        <div className="doc-split-form-item">
          <Button 
            variant="contained" 
            className="apply-button"
            sx={{
              background: "var(--hti-magenta)",
              color: "#FFF !important"
            }}
            disabled={disableForm}
            onClick={handleAddButton}
          >{isEdit ? "Update" : "Add"}</Button>
        </div>
      </div>
      {error && error?.length ? <div className="doc-split-form-error">{error}</div> : null}
      {formDetails?.sub_category == "Uncategorized" || formDetails?.sub_category == "Referral Consultant Report" ? <div className="doc-split-form-warning"><Warning /><span>{`Note: ${formDetails?.sub_category} documents cannot be uploaded.`}</span></div> : null}
    </div>
  );
};

export default DocumentSplitForm;